import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bars3Icon,
  XMarkIcon,
  UserIcon,
  PlusIcon,
  HeartIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import StatusBadge, { StatusIconComponent } from './StatusBadge';

const MenuButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.primary};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.surface};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const MenuOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${props => props.theme.zIndex.modal};
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
`;

const MenuPanel = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border-left: 1px solid ${props => props.theme.colors.border};
  width: 300px;
  height: 100vh;
  overflow-y: auto;
  padding: ${props => props.theme.spacing.lg};
`;

const MenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.xl};
  padding-bottom: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const MenuTitle = styled.h2`
  font-size: ${props => props.theme.typography.h3.fontSize};
  font-weight: ${props => props.theme.typography.h3.fontWeight};
  color: ${props => props.theme.colors.text.primary};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.surfaceVariant};
    color: ${props => props.theme.colors.text.primary};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const MenuSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const SectionTitle = styled.h3`
  font-size: ${props => props.theme.typography.small.fontSize};
  font-weight: 600;
  color: ${props => props.theme.colors.text.muted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  width: 100%;
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.primary};
  cursor: pointer;
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  text-align: left;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.surfaceVariant};
  }
  
  svg {
    width: 20px;
    height: 20px;
    color: ${props => props.theme.colors.text.secondary};
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.surfaceVariant};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const UserDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UserName = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.body.fontSize};
`;

const UserStats = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
`;

const ScenePoints = styled.div`
  font-size: ${props => props.theme.typography.small.fontSize};
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const HamburgerMenu = () => {
  const { user, userProfile, signOut, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleMenuItemClick = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Check if user can moderate - all authenticated users can moderate
  const canModerate = isAuthenticated;

  return (
    <>
      <MenuButton onClick={() => setIsOpen(true)}>
        <Bars3Icon />
      </MenuButton>

      <AnimatePresence>
        {isOpen && (
          <MenuOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          >
            <MenuPanel
              initial={{ x: 300 }}
              animate={{ x: 0 }}
              exit={{ x: 300 }}
              transition={{ type: 'tween', duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuHeader>
                <MenuTitle>Menu</MenuTitle>
                <CloseButton onClick={() => setIsOpen(false)}>
                  <XMarkIcon />
                </CloseButton>
              </MenuHeader>

              {isAuthenticated && userProfile && (
                <UserInfo>
                  {userProfile.user_level ? (
                    <StatusIconComponent level={userProfile.user_level} size="large" />
                  ) : (
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: '#8B5CF6',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '20px'
                    }}>
                      <UserIcon style={{ width: '24px', height: '24px' }} />
                    </div>
                  )}
                  <UserDetails>
                    <UserName>{userProfile.username || userProfile.display_name || 'User'}</UserName>
                    <UserStats>
                      <ScenePoints>{userProfile.scene_points ?? 0} Scene Points</ScenePoints>
                      {userProfile.user_level && (
                        <StatusBadge level={userProfile.user_level} showIcon={false} />
                      )}
                    </UserStats>
                  </UserDetails>
                </UserInfo>
              )}

              <MenuSection>
                <SectionTitle>Shows</SectionTitle>
                <MenuItem onClick={() => handleMenuItemClick('/add-show')}>
                  <PlusIcon />
                  Add Show
                </MenuItem>
                <MenuItem onClick={() => handleMenuItemClick('/my-shows')}>
                  <HeartIcon />
                  My Shows
                </MenuItem>
              </MenuSection>

              {canModerate && (
                <MenuSection>
                  <SectionTitle>Moderation</SectionTitle>
                  <MenuItem onClick={() => handleMenuItemClick('/moderation')}>
                    <ShieldCheckIcon />
                    Moderation
                  </MenuItem>
                </MenuSection>
              )}

              <MenuSection>
                <SectionTitle>Account</SectionTitle>
                <MenuItem onClick={() => handleMenuItemClick('/settings')}>
                  <Cog6ToothIcon />
                  Settings
                </MenuItem>
                {isAuthenticated && (
                  <MenuItem onClick={handleSignOut}>
                    <ArrowRightOnRectangleIcon />
                    Sign Out
                  </MenuItem>
                )}
              </MenuSection>

              <MenuSection>
                <SectionTitle>Help</SectionTitle>
                <MenuItem onClick={() => handleMenuItemClick('/faq')}>
                  <QuestionMarkCircleIcon />
                  FAQ
                </MenuItem>
              </MenuSection>
            </MenuPanel>
          </MenuOverlay>
        )}
      </AnimatePresence>
    </>
  );
};

export default HamburgerMenu; 